/**
 * ClearCacheDialog
 *
 * Confirmation dialog for clearing all local cache data.
 * This is an irreversible action that removes all offline data including
 * cached queries, file uploads, local photos, and service worker image cache.
 */

import { FC, useState } from 'react';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  SxProps,
  Theme,
} from '@mui/material';

import { useTranslation } from 'common/hooks/useTranslation';

export type ClearCacheDialogProps = {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when the dialog should close */
  onClose: () => void;
  /** Called when user confirms the clear cache action */
  onConfirm: () => Promise<void>;
};

const dialogStyles: SxProps<Theme> = {
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: 400,
  },
};

const titleStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  color: 'error.main',
};

const warningIconStyles: SxProps<Theme> = {
  color: 'warning.main',
  fontSize: 28,
};

const actionsStyles: SxProps<Theme> = {
  padding: 2,
  gap: 1,
};

export const ClearCacheDialog: FC<ClearCacheDialogProps> = ({ open, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [isClearing, setIsClearing] = useState(false);

  const handleConfirm = async () => {
    setIsClearing(true);
    try {
      await onConfirm();
    } finally {
      setIsClearing(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={isClearing ? undefined : onClose} sx={dialogStyles}>
      <DialogTitle sx={titleStyles}>
        <WarningAmberIcon sx={warningIconStyles} />
        {t('cache.clear.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{t('cache.clear.message')}</DialogContentText>
        <DialogContentText sx={{ mt: 2, fontWeight: 'bold', color: 'error.main' }}>
          {t('cache.clear.warning')}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={actionsStyles}>
        <Button variant="outlined" onClick={onClose} disabled={isClearing}>
          {t('general.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={isClearing}
          startIcon={isClearing ? <CircularProgress size={16} color="inherit" /> : <DeleteForeverIcon />}
        >
          {isClearing ? t('cache.clear.clearing') : t('cache.clear.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
